import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, Utensils, PartyPopper, Star } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

const iconMap = {
  Heart: Heart,
  Sparkles: Sparkles,
  Utensils: Utensils,
  PartyPopper: PartyPopper,
  Ring: Heart,
};

const heroImages = [
  'https://images.unsplash.com/photo-1519741497674-611481863552?w=1920',
  'https://images.unsplash.com/photo-1763553113391-a659bee36e06?w=1920',
  'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1920',
  'https://images.unsplash.com/photo-1755795652039-c95221cc55fa?w=1920',
  'https://images.unsplash.com/photo-1507504031003-b417219a0fde?w=1920',
];

const HomePage = () => {
  const [siteContent, setSiteContent] = useState(null);
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, servicesRes, portfolioRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/site-content`),
          axios.get(`${API}/services`),
          axios.get(`${API}/portfolio`),
          axios.get(`${API}/testimonials`),
        ]);
        setSiteContent(contentRes.data);
        setServices(servicesRes.data);
        setPortfolio(portfolioRes.data.slice(0, 6));
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImage((prev) => (prev + 1) % heroImages.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextImage, 5000);
    return () => clearInterval(interval);
  }, [nextImage]);

  const RevealSection = ({ direction = 'left', children }) => {
    const { ref, style } = useScrollReveal(direction);
    return <div ref={ref} style={style}>{children}</div>;
  };

  return (
    <div data-testid="home-page" className="bg-[#F5F5F0]">
      {/* Hero Section - Image Slideshow */}
      <section data-testid="hero-section" className="relative h-screen overflow-hidden">
        {/* Image Slideshow Background */}
        <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
              style={{ opacity: currentImage === index ? 1 : 0 }}
            >
              <img
                src={img}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <div className="mb-6 animate-fade-in-up relative">
            <div className="absolute inset-0 bg-white blur-[80px] rounded-full scale-150" />
            <img 
              src="/logo-home.png"
              alt="Eden's Dawn"
              className="relative h-32 md:h-40 lg:h-48 w-auto"
            />
          </div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light mb-4 animate-fade-in-up animation-delay-200">
            {siteContent?.hero_tagline || 'Where beautiful beginnings are created'}
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mb-10 animate-fade-in-up animation-delay-300">
            {siteContent?.hero_subtitle || 'Elegant Event Planning in The Bahamas'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
            <Link to="/contact">
              <Button
                data-testid="hero-inquiry-btn"
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8 py-6 text-lg shadow-gold"
              >
                Begin Your Journey
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                data-testid="hero-portfolio-btn"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#2C2C2C] rounded-full px-8 py-6 text-lg"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <RevealSection direction="left">
      <section data-testid="intro-section" className="section-padding bg-[#F5F5F0]">
        <div className="container-custom text-center">
          <span className="font-accent text-4xl text-[#D4AF37]">Welcome to</span>
          <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2 mb-6">
            Eden's Dawn Wedding Events and Creations
          </h2>
          <div className="decorative-line" />
          <p className="text-lg text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
            We combine creativity, personal attention and thoughtful design to create beautiful and meaningful events 
            that reflect each client's vision and personality. Based in the beautiful Bahamas, we bring the essence 
            of paradise to every celebration we craft.
          </p>
        </div>
      </section>
      </RevealSection>

      {/* Services Preview */}
      <RevealSection direction="right">
      <section data-testid="services-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-3xl text-[#D4AF37]">What We Do</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2">Our Services</h2>
            <div className="decorative-line" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 5).map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <div
                  key={service.id}
                  data-testid={`service-card-${index}`}
                  className={`group relative bg-[#F5F5F0] rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-hover ${
                    index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                >
                  <div className={`relative ${index === 0 ? 'h-[400px] md:h-full' : 'h-64'} img-hover-zoom`}>
                    <img
                      src={service.image_url}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                        <IconComponent className="text-white" size={18} />
                      </div>
                      <h3 className="font-heading text-2xl">{service.title}</h3>
                    </div>
                    <p className="text-white/80 text-sm line-clamp-2">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="view-services-btn"
                className="bg-[#4A5D23] hover:bg-[#6B7F3A] text-white rounded-full px-8"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Featured Portfolio */}
      <RevealSection direction="left">
      <section data-testid="portfolio-section" className="section-padding bg-[#F5F5F0]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-3xl text-[#D4AF37]">Our Work</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2">Featured Events</h2>
            <div className="decorative-line" />
          </div>

          <div className="bento-grid">
            {portfolio.map((item, index) => (
              <div
                key={item.id}
                data-testid={`portfolio-item-${index}`}
                className={`relative overflow-hidden rounded-2xl group cursor-pointer img-hover-zoom ${
                  index === 0 || index === 3 ? 'bento-item-large' : ''
                }`}
                style={{ aspectRatio: index === 0 || index === 3 ? '1' : '3/4' }}
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <span className="text-[#D4AF37] text-sm tracking-wider uppercase mb-2">
                    {item.category}
                  </span>
                  <h3 className="font-heading text-2xl text-white">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button
                data-testid="view-portfolio-btn"
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white rounded-full px-8"
              >
                View Full Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Testimonials */}
      <RevealSection direction="right">
      <section data-testid="testimonials-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-3xl text-[#D4AF37]">Kind Words</span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2">Client Love</h2>
            <div className="decorative-line" />
          </div>

          <Carousel className="max-w-4xl mx-auto">
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={testimonial.id}>
                  <div
                    data-testid={`testimonial-${index}`}
                    className="relative bg-[#F5F5F0] rounded-2xl px-8 py-12 md:px-16 text-center"
                  >
                    <div className="absolute top-6 left-8 text-[#D4AF37] opacity-20 font-accent text-9xl leading-none">
                      "
                    </div>
                    <div className="flex justify-center mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
                      ))}
                    </div>
                    <p className="font-heading text-xl md:text-2xl text-[#2C2C2C] italic leading-relaxed mb-8 relative z-10">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <img
                        src={testimonial.image_url}
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]"
                      />
                      <div className="text-left">
                        <h4 className="font-heading text-lg text-[#2C2C2C]">{testimonial.name}</h4>
                        <p className="text-sm text-[#6B7280]">{testimonial.event_type}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12" />
            <CarouselNext className="hidden md:flex -right-12" />
          </Carousel>
        </div>
      </section>
      </RevealSection>

      {/* CTA Section */}
      <RevealSection direction="up">
      <section data-testid="cta-section" className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1617170709050-d4d00fe97b4c?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/80" />
        <div className="relative z-10 container-custom text-center text-white">
          <span className="font-accent text-4xl text-[#D4AF37]">Ready to Begin?</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-2 mb-6">Let's Create Something Beautiful</h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
            Your special event deserves extraordinary attention. Contact us today and let's start bringing your vision to life.
          </p>
          <Link to="/contact">
            <Button
              data-testid="cta-btn"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-10 py-6 text-lg shadow-gold"
            >
              Start Planning Your Event
            </Button>
          </Link>
        </div>
      </section>
      </RevealSection>
    </div>
  );
};

export default HomePage;
